"use client";

import React from "react";
import {
  ClerkProvider,
  SignIn as ClerkSignIn,
  SignUp as ClerkSignUp,
  SignedIn as ClerkSignedIn,
  SignedOut as ClerkSignedOut,
  RedirectToSignIn as ClerkRedirectToSignIn,
  UserProfile as ClerkUserProfile,
  OrganizationProfile as ClerkOrganizationProfile,
  CreateOrganization as ClerkCreateOrganization,
} from "@clerk/nextjs";
import { dark, experimental__simple } from "@clerk/themes";
import { useAppTheme } from "@/contexts/AppThemeContext";
import {
  AuthProviderNameProvider,
  useAuthProviderName,
} from "@/lib/auth/providerContext";
import { getClerkVariables } from "@/lib/auth/clerk/getClerkVariables";
import { getClerkLayout } from "@/lib/auth/clerk/getClerkLayout";
import {
  OidcAuthProvider,
  OidcSignedIn,
  OidcSignedOut,
  OidcRedirectToSignIn,
  OidcSignIn,
  OidcSignUp,
  OidcUnavailableWidget,
} from "@/lib/auth/oidc/components";

// Type compatibility bridge for different Clerk versions
interface ClerkAppearance {
  baseTheme?: unknown;
  variables?: Record<string, unknown>;
  layout?: Record<string, unknown>;
}

const getClerkTheme = (isDarkMode: boolean) =>
  isDarkMode ? dark : experimental__simple;

// Provider Components
/**
 * Mounts the identity provider's own React context, and publishes which
 * provider that is to everything below.
 *
 * `provider` comes from the ROOT LAYOUT, which is a server component and can
 * therefore read AUTH_PROVIDER at request time. Without it this falls back to
 * the build-time NEXT_PUBLIC_AUTH_PROVIDER, which is what apps that have not
 * adopted the prop keep doing.
 */
export const AuthProvider = ({
  provider: providerProp,
  children,
}: React.PropsWithChildren<{ provider?: string }>) => (
  <AuthProviderNameProvider provider={providerProp}>
    <AuthProviderInner>{children}</AuthProviderInner>
  </AuthProviderNameProvider>
);

const AuthProviderInner = ({ children }: React.PropsWithChildren) => {
  const provider = useAuthProviderName();
  const { isDarkMode, muiTheme } = useAppTheme();
  const clerkTheme = getClerkTheme(isDarkMode);

  switch (provider) {
    case "dummy":
      return <div data-testid="dummy-auth-provider">{children}</div>;
    case "keycloak":
    case "oidc":
      return <OidcAuthProvider>{children}</OidcAuthProvider>;
    case "clerk":
    default:
      return (
        <ClerkProvider
          appearance={
            {
              baseTheme: clerkTheme,
              variables: getClerkVariables(muiTheme),
              layout: getClerkLayout(muiTheme),
            } satisfies ClerkAppearance
          }
        >
          {children}
        </ClerkProvider>
      );
  }
};

// Auth State Components
export const SignedIn = ({ children }: { children: React.ReactNode }) => {
  const provider = useAuthProviderName();

  switch (provider) {
    case "dummy":
      return <div data-testid="dummy-signed-in">{children}</div>;
    case "keycloak":
    case "oidc":
      return <OidcSignedIn>{children}</OidcSignedIn>;
    case "clerk":
    default:
      return <ClerkSignedIn>{children}</ClerkSignedIn>;
  }
};

export const SignedOut = ({ children }: { children: React.ReactNode }) => {
  const provider = useAuthProviderName();

  switch (provider) {
    case "dummy":
      return null; // In dummy mode, user is always "signed in"
    case "keycloak":
    case "oidc":
      return <OidcSignedOut>{children}</OidcSignedOut>;
    case "clerk":
    default:
      return <ClerkSignedOut>{children}</ClerkSignedOut>;
  }
};

export const RedirectToSignIn = () => {
  const provider = useAuthProviderName();

  switch (provider) {
    case "dummy":
      return <div data-testid="dummy-redirect">Redirecting to sign in...</div>;
    case "keycloak":
    case "oidc":
      return <OidcRedirectToSignIn />;
    case "clerk":
    default:
      return <ClerkRedirectToSignIn />;
  }
};

// Auth Form Components
export const SignIn = () => {
  const provider = useAuthProviderName();
  const { isDarkMode, muiTheme } = useAppTheme();
  const clerkTheme = getClerkTheme(isDarkMode);

  switch (provider) {
    case "dummy":
      return <div data-testid="dummy-sign-in">Dummy Sign In Form</div>;
    case "keycloak":
    case "oidc":
      // No embeddable widget: the login form is a page on the identity
      // provider, so "showing the sign-in UI" means starting the redirect.
      return <OidcSignIn />;
    case "clerk":
    default:
      return (
        <ClerkSignIn
          appearance={
            {
              baseTheme: clerkTheme,
              variables: getClerkVariables(muiTheme),
              layout: getClerkLayout(muiTheme),
            } satisfies ClerkAppearance
          }
        />
      );
  }
};

export const SignUp = () => {
  const provider = useAuthProviderName();
  const { isDarkMode, muiTheme } = useAppTheme();
  const clerkTheme = getClerkTheme(isDarkMode);

  switch (provider) {
    case "dummy":
      return <div data-testid="dummy-sign-up">Dummy Sign Up Form</div>;
    case "keycloak":
    case "oidc":
      return <OidcSignUp />;
    case "clerk":
    default:
      return (
        <ClerkSignUp
          signInUrl="/sign-in"
          appearance={
            {
              baseTheme: clerkTheme,
              variables: getClerkVariables(muiTheme),
              layout: getClerkLayout(muiTheme),
            } satisfies ClerkAppearance
          }
        />
      );
  }
};

// Profile Components
export const UserProfile = (
  props: React.ComponentProps<typeof ClerkUserProfile>,
) => {
  const provider = useAuthProviderName();

  switch (provider) {
    case "dummy":
      return <div data-testid="dummy-user-profile">Dummy User Profile</div>;
    case "keycloak":
    case "oidc":
      return <OidcUnavailableWidget what="Profile management" />;
    case "clerk":
    default:
      return <ClerkUserProfile {...props} />;
  }
};

export const OrganizationProfile = (
  props: React.ComponentProps<typeof ClerkOrganizationProfile>,
) => {
  const provider = useAuthProviderName();

  switch (provider) {
    case "dummy":
      return (
        <div data-testid="dummy-org-profile">Dummy Organization Profile</div>
      );
    case "keycloak":
    case "oidc":
      return <OidcUnavailableWidget what="Organization management" />;
    case "clerk":
    default:
      return <ClerkOrganizationProfile {...props} />;
  }
};

export const CreateOrganization = (
  props: React.ComponentProps<typeof ClerkCreateOrganization>,
) => {
  const provider = useAuthProviderName();

  switch (provider) {
    case "dummy":
      return (
        <div data-testid="dummy-create-org">Dummy Create Organization</div>
      );
    case "keycloak":
    case "oidc":
      return <OidcUnavailableWidget what="Organization creation" />;
    case "clerk":
    default:
      return <ClerkCreateOrganization {...props} />;
  }
};
